import { isMobile } from 'react-device-detect'
import { getSttEngine, sttEngineLabel } from '../labs/sttEngine'
import { TopNav } from './TopNav'

// Ad layout, replicated EXACTLY from the maffdoku family (../maffdoku/src/App.tsx
// lines ~3605–4082) so behaviour matches:
//   - outer `flex flex-row justify-center`
//   - desktop side rails (nerdlegame_D_x1 / _D_x2), gated `!isMobile && !disableAds`,
//     using the `.desktopSideAd` (flex:3) treatment so they fill leftover space
//   - a FIXED 450px centre column (`max-w-[100%] w-[450px]`) that the rails never
//     squeeze — this is what keeps the game from going narrow
//   - in-content banner (nerdlegame_D_1 / _M_1) with the dev-host placeholder that
//     shows the reserved ad height (250px tall / 150px short) on localhost
// lngtd's nerdlegame.js (loaded in index.html) injects the actual creatives.

const adsDisabled = (): boolean =>
  typeof window !== 'undefined' && Boolean(localStorage.getItem('disableAdsTimestamp'))

// Rough per-minute rate for the cost estimate in the debug readout (Deepgram Nova-2
// streaming, approx — adjust to your contract). Clearly labelled "est" in the UI.
const STT_RATE_PER_MIN: Record<string, number> = { deepgram: 0.0059, google: 0.016 }

export const AdLayout = ({
  children,
  debugInfo,
}: {
  children: React.ReactNode
  debugInfo?: { audioSeconds?: number }
}) => {
  const disableAds = adsDisabled()

  // Dev-only ad-slot placeholder so localhost preview shows the reserved height.
  // Empty style on production — the prod slot flexes to whatever creative serves.
  const _isDevHost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  const _adReservation = typeof window !== 'undefined' && window.innerHeight >= 810 ? 250 : 150

  // Debug readout lives INSIDE the dev ad-slot box (localhost only — never production).
  const engine = getSttEngine()
  const audioSeconds = debugInfo?.audioSeconds ?? 0
  const rate = STT_RATE_PER_MIN[engine] ?? 0
  const estCost = (audioSeconds / 60) * rate
  const _placeholderStyle: React.CSSProperties = _isDevHost
    ? {
        minHeight: _adReservation,
        border: '2px dashed #820458',
        background: 'rgba(130, 4, 88, 0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#820458',
        fontSize: '0.85rem',
        fontFamily: 'monospace',
      }
    : {}

  return (
    <div className="flex flex-row justify-center">
      {!isMobile && !disableAds && (
        <div id="nerdlegame_D_x1" className="desktopSideAd mr-8 ml-4"></div>
      )}

      {/* pb reserves space for the sticky bottom anchor ad the ad script pins to the viewport, so
          the last control can always scroll clear of it (otherwise it sits behind the ad at the
          bottom of the page with nothing below it to scroll into view). */}
      <div className="max-w-[100%] w-[450px] items-center pb-44">
        <TopNav />
        {children}

        {!isMobile && !disableAds && (
          <div className="mt-4 max-w-[90%] mx-auto justify-center items-center text-center">
            <div id="nerdlegame_D_1"></div>
            <div id="nerdlegame_M_1" style={_placeholderStyle}>
              {_isDevHost && (
                <div className="flex flex-col items-center gap-1 leading-tight">
                  <span>[ad slot — {_adReservation}px]</span>
                  <span>transcription: {sttEngineLabel(engine)}</span>
                  {engine !== 'webspeech' && (
                    <span>
                      audio {audioSeconds.toFixed(1)}s · ~${estCost.toFixed(4)} est
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {!isMobile && !disableAds && (
        <div id="nerdlegame_D_x2" className="desktopSideAd ml-8 mr-4"></div>
      )}
    </div>
  )
}
